import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Establishment } from 'app/modules/features/models/establishment';
import { EstablishmentService } from 'app/modules/features/services/establishment.service';
import { FeaturesRefService } from 'app/modules/features/services/features-ref.service';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { PartnerService } from 'app/modules/partner/services/partner.service';
import { Partner } from 'app/modules/shared/models/partner';
import { ObjectRef } from 'app/modules/shared/models/objectRef';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { Subscription as PartnerSubscription } from 'app/modules/shared/models/subscription';
import { NotificationService } from 'app/modules/shared/services/notification.service';
import { SpinnerService } from 'app/modules/shared/services/spinner.service';
import { UpgradeService } from 'app/modules/shared/services/upgrade.service';

import {
    AddEstablishmentDialogComponent,
    AddEstablishmentDialogResult,
} from './add-establishment-dialog.component';

/** Backend error code when a non-PRO (or non-flagged) partner tries to add an establishment */
const ERR_MULTI_ESTABLISHMENT_NOT_ENABLED = '31101';
/** Backend error code when the language list exceeds the plan limit (shared with add/initialize flows) */
const ERR_PLAN_LIMIT_LANGUAGE_EXCEEDED = '30905';
/** Backend error code when the partner has reached the hard cap on establishments */
const ERR_ESTABLISHMENT_LIMIT_REACHED = '31107';
/** Backend error code when the requested slug is already in use by another establishment */
const ERR_SLUG_ALREADY_EXISTS = '30203';

/**
 * Hard cap on establishments per partner (duplicates the backend constant for UX feedback).
 * Keep in sync with `EstablishmentServiceImpl.MAX_ESTABLISHMENTS_PER_PARTNER`.
 */
const MAX_ESTABLISHMENTS = 3;

/**
 * Toolbar dropdown that lets a partner switch between their establishments,
 * and (when their plan/flag allows) create a new one.
 */
@Component({
    selector: 'establishment-switcher',
    templateUrl: './establishment-switcher.component.html',
    styleUrls: ['./establishment-switcher.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        TranslocoModule,
    ],
})
export class EstablishmentSwitcherComponent implements OnInit, OnDestroy {
    user: User | null = null;
    partner: Partner | null = null;
    establishments: Establishment[] = [];
    currentEstablishment: Establishment | null = null;
    subscription: PartnerSubscription | null = null;
    /** Menu languages reference list — loaded once on init, reused each time the dialog opens */
    private _availableLanguages: ObjectRef[] = [];
    /** Ids of establishments whose logo URL failed to load — fall back to initials */
    private _logoFailedIds = new Set<number>();
    /** Establishment type reference list — preloaded so the quick-create dialog opens populated */
    private _establishmentTypeList: ObjectRef[] = [];
    /** Capacity reference list — preloaded so the quick-create dialog opens populated */
    private _capacityRefList: ObjectRef[] = [];

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _cdr: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _router: Router,
        private _translocoService: TranslocoService,
        private _userService: UserService,
        private _partnerService: PartnerService,
        private _subscriptionService: SubscriptionService,
        private _establishmentService: EstablishmentService,
        private _featuresRefService: FeaturesRefService,
        private _notificationService: NotificationService,
        private _spinnerService: SpinnerService,
        private _upgradeService: UpgradeService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * The plan code effectively applied to the partner (resolved from subscription first, then partner).
     */
    get planCode(): string | null {
        if (this.subscription?.planCode) {
            return this.subscription.planCode;
        }
        if (this.partner?.subscribePlanCodeSelected) {
            return this.partner.subscribePlanCodeSelected as string;
        }
        return null;
    }

    get isPro(): boolean {
        return this.planCode === PlanEnum.PRO;
    }

    /**
     * True when the partner has the multi-establishment capability (flag + plan).
     */
    get multiEstablishmentEnabled(): boolean {
        // Prefer partner model (fresh), fall back to user denormalised flag for early bootstrap
        return !!(this.partner?.multiEstablishmentEnabled ?? this.user?.multiEstablishmentEnabled);
    }

    /**
     * Visibility of the entire switcher button in the toolbar.
     * Show when:
     *  - multiEstablishmentEnabled is true (so the partner can at least see the add button), OR
     *  - partner owns more than one establishment (always let them switch even if flag is later revoked)
     */
    get shouldShow(): boolean {
        if (!this.user || !this.establishments?.length) {
            return false;
        }
        if (this.establishments.length > 1) {
            return true;
        }
        return this.multiEstablishmentEnabled;
    }

    /**
     * Whether the "Add establishment" action is allowed right now.
     */
    get canAdd(): boolean {
        return this.isPro && this.multiEstablishmentEnabled;
    }

    /**
     * Whether the partner is still under the hard cap on establishments.
     * Used alongside `canAdd` to disable the action row once the limit is reached.
     */
    get canAddMore(): boolean {
        return (this.establishments?.length ?? 0) < MAX_ESTABLISHMENTS;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user) => {
                this.user = user;
                this._cdr.markForCheck();
            });

        this._partnerService.partner$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((partner) => {
                this.partner = partner;
                this._cdr.markForCheck();
            });

        this._subscriptionService.partnerSubscription$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((subscription) => {
                this.subscription = subscription;
                this._cdr.markForCheck();
            });

        this._establishmentService.establishments$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((list) => {
                this.establishments = list || [];
                this._cdr.markForCheck();
            });

        this._establishmentService.establishment$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((establishment) => {
                this.currentEstablishment = establishment;
                this._cdr.markForCheck();
            });

        // Preload the menu languages reference list so the "Add establishment" dialog
        // can open instantly with a pre-populated multi-select.
        this._featuresRefService.getMenuLanguagesRef()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (languages: any) => {
                    this._availableLanguages = Array.isArray(languages) ? languages : [];
                },
                error: () => {
                    // Silent — the dialog simply shows no options and submit stays blocked by validator
                    this._availableLanguages = [];
                },
            });

        // Preload establishment type and capacity ref lists for the same reason.
        this._featuresRefService.getEstablishmentTypeRef()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (types) => {
                    this._establishmentTypeList = Array.isArray(types) ? types : [];
                },
                error: () => { this._establishmentTypeList = []; },
            });
        this._featuresRefService.getCapacityRef()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (capacities) => {
                    this._capacityRefList = Array.isArray(capacities) ? capacities : [];
                },
                error: () => { this._capacityRefList = []; },
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Whether the given establishment is the currently selected one.
     */
    isCurrent(establishment: Establishment): boolean {
        return this.currentEstablishment?.id === establishment.id;
    }

    /**
     * Whether to render the logo `<img>` for the given establishment.
     * False when the logo key is empty OR has previously failed to load.
     */
    shouldShowLogo(establishment: Establishment | null): boolean {
        if (!establishment?.logo) {
            return false;
        }
        return !this._logoFailedIds.has(establishment.id);
    }

    /**
     * Build the full public URL for the establishment's logo.
     * The `logo` field stored on the establishment is just the S3 key — the
     * `environment.bucket` prefix is what makes it a fetchable URL (same
     * convention as `appearance.component.ts`).
     */
    logoUrl(establishment: Establishment | null): string {
        if (!establishment?.logo) {
            return '';
        }
        return [environment.bucket, establishment.logo].join('');
    }

    /**
     * Called by the `<img (error)>` handler when a logo URL fails to load.
     * Records the establishment id so the template falls back to the colored initials.
     */
    onLogoError(establishment: Establishment | null): void {
        if (!establishment?.id) {
            return;
        }
        this._logoFailedIds.add(establishment.id);
        this._cdr.markForCheck();
    }

    /**
     * Short initial displayed as a fallback when no logo is set.
     */
    initialFor(establishment: Establishment | null): string {
        const name = (establishment?.name || '').trim();
        return name ? name.charAt(0).toUpperCase() : '?';
    }

    /**
     * Up to 2 uppercase initials derived from the establishment name.
     * Examples: "Chez Shevatechs" → "CS"; "Pizza" → "P"; "" → "?".
     */
    twoInitialsFor(establishment: Establishment | null): string {
        const name = (establishment?.name || '').trim();
        if (!name) {
            return '?';
        }
        const words = name.split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            return '?';
        }
        if (words.length === 1) {
            return words[0].charAt(0).toUpperCase();
        }
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }

    /**
     * Deterministic Tailwind background colour for the initials avatar.
     * Hashes the establishment id (or name as a fallback) into one of 5 palette slots.
     */
    colorFor(establishment: Establishment | null): string {
        const palette = [
            'bg-indigo-500',
            'bg-emerald-500',
            'bg-amber-500',
            'bg-rose-500',
            'bg-sky-500',
        ];
        if (!establishment) {
            return palette[0];
        }
        let seed: number;
        if (typeof establishment.id === 'number' && !Number.isNaN(establishment.id)) {
            seed = Math.abs(establishment.id);
        } else {
            // Simple string hash (djb2-ish) for deterministic colour from the name.
            const name = establishment.name || '';
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = (hash * 31 + name.charCodeAt(i)) | 0;
            }
            seed = Math.abs(hash);
        }
        return palette[seed % palette.length];
    }

    /**
     * Switch to another establishment. No-op if it's already the current one.
     */
    switchTo(establishment: Establishment): void {
        if (!this.user?.partnerId || !establishment?.id) {
            return;
        }
        if (this.isCurrent(establishment)) {
            return;
        }

        this._spinnerService.show();
        this._establishmentService
            .setCurrentEstablishment(this.user.partnerId, establishment.id)
            .subscribe({
                next: (updated) => {
                    this._spinnerService.hide();
                    // Mirror the new id on the in-memory user for future bootstraps
                    if (this.user) {
                        this._userService.user = { ...this.user, currentEstablishmentId: updated.id };
                    }
                    this._cdr.markForCheck();
                },
                error: () => {
                    this._spinnerService.hide();
                    this._notificationService.error(
                        this._translocoService.translate('features.establishment-switcher.error-generic'),
                    );
                },
            });
    }

    /**
     * Open the "Add establishment" dialog, or trigger the upgrade flow if gating fails.
     */
    onAddEstablishmentClick(): void {
        if (!this.isPro) {
            // Route the user through the standard upgrade UX
            this._upgradeService.openUpgradeModal();
            return;
        }
        if (!this.multiEstablishmentEnabled) {
            // Plan is PRO but backoffice flag not enabled — show a clear notice
            this._notificationService.info(
                this._translocoService.translate('features.establishment-switcher.add-dialog.error-not-enabled'),
            );
            return;
        }
        this._openAddDialog();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _openAddDialog(): void {
        // Seed the language multi-select from the current establishment (fallback to the
        // oldest establishment in the list if the current one isn't loaded yet).
        const seedEstablishment = this.currentEstablishment
            || (this.establishments?.length ? this.establishments[0] : null);
        const preselectedLanguageCodes = (seedEstablishment?.establishmentLanguages || [])
            .map((l: any) => l.languageRefCode)
            .filter(Boolean);

        const ref = this._dialog.open(AddEstablishmentDialogComponent, {
            width: '480px',
            maxWidth: '95vw',
            autoFocus: 'first-tabbable',
            data: {
                disabled: false,
                availableLanguages: this._availableLanguages,
                preselectedLanguageCodes,
                planCode: this.planCode || PlanEnum.PRO,
                hasUnlimitedLanguages: !!this.partner?.unlimitedLanguages,
                establishmentTypeList: this._establishmentTypeList,
                capacityRefList: this._capacityRefList,
                partnerId: this.user?.partnerId,
            },
        });

        ref.afterClosed().subscribe((result: AddEstablishmentDialogResult | undefined) => {
            if (!result || !this.user?.partnerId) {
                return;
            }
            this._createAdditionalEstablishment(this.user.partnerId, result);
        });
    }

    private _createAdditionalEstablishment(partnerId: number, payload: AddEstablishmentDialogResult): void {
        this._spinnerService.show();
        this._establishmentService
            .createAdditionalEstablishment(partnerId, payload)
            .subscribe({
                next: (created) => {
                    // Switch to the new establishment server-side, then send the partner
                    // straight to the menus page. The quick-create dialog already collected
                    // all the essentials (name, slug, type, capacity, languages) so the
                    // onboarding wizard is no longer required for additional establishments.
                    this._establishmentService.setCurrentEstablishment(partnerId, created.id).subscribe({
                        next: () => {
                            this._spinnerService.hide();
                            this._notificationService.success(
                                this._translocoService.translate(
                                    'features.establishment-switcher.add-dialog.success',
                                ),
                            );
                            if (this.user) {
                                this._userService.user = {
                                    ...this.user,
                                    currentEstablishmentId: created.id,
                                };
                            }
                            this._router.navigate(['/', PathEnum.APP, PathEnum.APP_PARTNER_MENU]);
                        },
                        error: () => {
                            this._spinnerService.hide();
                            this._notificationService.error(
                                this._translocoService.translate('features.establishment-switcher.error-generic'),
                            );
                        },
                    });
                },
                error: (err) => {
                    this._spinnerService.hide();
                    if (this._isEstablishmentLimitError(err)) {
                        this._notificationService.error(
                            this._translocoService.translate(
                                'features.establishment-switcher.add-dialog.error-limit-reached',
                            ),
                        );
                    } else if (this._isNotEnabledError(err)) {
                        this._notificationService.error(
                            this._translocoService.translate(
                                'features.establishment-switcher.add-dialog.error-not-enabled',
                            ),
                        );
                    } else if (this._isLanguageLimitError(err)) {
                        // Reuse the same translation used by the add/initialize-languages flows
                        this._notificationService.error(
                            this._translocoService.translate('limits.plan_limit_reached_languages'),
                        );
                    } else if (this._isSlugTakenError(err)) {
                        this._notificationService.error(
                            this._translocoService.translate(
                                'features.establishment-switcher.add-dialog.slug-taken',
                            ),
                        );
                    } else {
                        this._notificationService.error(
                            this._translocoService.translate('features.establishment-switcher.error-generic'),
                        );
                    }
                },
            });
    }

    /**
     * Detect the backend "multi-establishment not enabled" error (code 31101).
     * The backend packs error codes into err.error.details[0] (same convention as payment errors).
     */
    private _isNotEnabledError(err: any): boolean {
        const codeFromDetails = err?.error?.details?.[0];
        const codeFromBody = err?.error?.code;
        return (
            codeFromDetails === ERR_MULTI_ESTABLISHMENT_NOT_ENABLED ||
            codeFromBody === ERR_MULTI_ESTABLISHMENT_NOT_ENABLED
        );
    }

    /**
     * Detect the backend "language limit exceeded" error (code 30905) — thrown when
     * the selected language count exceeds what the partner's plan allows.
     */
    private _isLanguageLimitError(err: any): boolean {
        const codeFromDetails = err?.error?.details?.[0];
        const codeFromBody = err?.error?.code;
        return (
            codeFromDetails === ERR_PLAN_LIMIT_LANGUAGE_EXCEEDED ||
            codeFromBody === ERR_PLAN_LIMIT_LANGUAGE_EXCEEDED
        );
    }

    /**
     * Detect the backend "establishment limit reached" error (code 31107) — thrown when
     * the partner already owns the maximum number of establishments allowed.
     */
    private _isEstablishmentLimitError(err: any): boolean {
        const codeFromDetails = err?.error?.details?.[0];
        const codeFromBody = err?.error?.code;
        return (
            codeFromDetails === ERR_ESTABLISHMENT_LIMIT_REACHED ||
            codeFromBody === ERR_ESTABLISHMENT_LIMIT_REACHED
        );
    }

    /**
     * Detect the backend "slug already exists" error (code 30203) — thrown when two
     * partners race for the same slug after the async availability check succeeded.
     */
    private _isSlugTakenError(err: any): boolean {
        const codeFromDetails = err?.error?.details?.[0];
        const codeFromBody = err?.error?.code;
        return (
            codeFromDetails === ERR_SLUG_ALREADY_EXISTS ||
            codeFromBody === ERR_SLUG_ALREADY_EXISTS
        );
    }
}
